import { ConsumableContract, PutPostUtilityConsumable } from "@/api/facilities";
import ConsumableModal, { ConsumableSelection } from "@/components/ConsumableModal/ConsumableModal";
import useAnalytics from "@/hooks/useAnalytics";
import { useCreateUtilityConsumable, useUpdateUtilityConsumable } from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { message } from "antd";
import dayjs from "dayjs";

export default function UtilityConsumableModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: ConsumableContract;
  minDate: dayjs.Dayjs;
  maxDate: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const createUtilityConsumable = useCreateUtilityConsumable();
  const updateUtilityConsumable = useUpdateUtilityConsumable();
  const analytics = useAnalytics({ enabled: !publishedId });
  const translation = useTranslation();

  const onChange = (value: ConsumableSelection) => {
    const contractToPutPost: PutPostUtilityConsumable = {
      facility_id: facilityId,
      start_date: minDate.startOf("month").format("YYYY-MM-DD"),
      end_date: maxDate.endOf("month").format("YYYY-MM-DD"),
      contract_type: "actual",
      ...value,
    };

    if (contract) {
      updateUtilityConsumable
        .mutateAsync({
          contractId: contract.id,
          contract: contractToPutPost,
        })
        .then((result) => {
          if (result.status === 200) {
            message.success("Consumable updated.");
            analytics.track("Contract Updated");

            closeModal();
          }
        })
        .catch(() => message.error(translation.errors.general));
    } else {
      createUtilityConsumable
        .mutateAsync({
          contract: contractToPutPost,
        })
        .then((result) => {
          if (result.status === 200) {
            analytics.track("Contract Added");
            message.success("Consumable added.");
            closeModal();
          }
        })
        .catch(() => message.error(translation.errors.general));
    }
  };

  const source =
    contract?.sources && contract.sources.length > 0 ? contract?.sources[0] : undefined;

  return (
    <ConsumableModal
      value={source}
      onChange={(value) => value && onChange(value)}
      isOpen={open}
      setIsOpen={(value) => !value && closeModal()}
      okText={contract ? "Save" : "Add consumable"}
      totalConsumabedLabel="Total consumed in facility"
      headerText={contract ? "Edit consumable" : "Add consumable"}
    />
  );
}
